import {
  type ChatbotEvent,
  type ReopenEvent,
  type TransferEvent,
  listClosingReasons,
  listMessages,
  listTags,
} from '@/features/atendimento/api';
import { MessageBubble } from '@/features/atendimento/components/MessageBubble';
import {
  dentroDoBlocoCarregado,
  pisoDosBanners,
} from '@/features/atendimento/eventos-do-bloco-carregado';
import { dayLabel } from '@/features/atendimento/lib/format';
import type { ChatMessage } from '@/features/atendimento/types';
import { listChannels } from '@/features/channels/api';
import { listDepartments, listUsers } from '@/features/configuracoes/api';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { useEscapeClose } from '@/shared/hooks/useEscapeClose';
import { useAuthStore } from '@/shared/stores/auth-store';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import {
  ArrowRightLeft,
  Bot,
  CalendarClock,
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
  Download,
  Loader2,
  MessageCircleMore,
  MessageSquareText,
  Printer,
  Search as SearchIcon,
  Undo2,
  X,
} from 'lucide-react';
import { Fragment, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { type SearchFilters, type SearchTicket, exportConsultation, searchTickets } from './api';

/** Data local de hoje em formato `YYYY-MM-DD` (não usa toISOString — UTC quebra em fuso). */
function todayLocalYMD(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Filtros default — pré-preenche período com o dia de hoje (BRT). */
function defaultFilters(): SearchFilters {
  const today = todayLocalYMD();
  return {
    date_range: {
      start: new Date(`${today}T00:00:00`).toISOString(),
      end: new Date(`${today}T23:59:59.999`).toISOString(),
    },
  };
}

/** Escala da Pesquisa de Satisfação — rótulos usados no filtro e na coluna. */
const RATING_OPTIONS: Array<{ value: number; label: string }> = [
  { value: 5, label: '(5) - Excelente 🤩' },
  { value: 4, label: '(4) - Bom 😍' },
  { value: 3, label: '(3) - Neutro 😐' },
  { value: 2, label: '(2) - Ruim 🥺' },
  { value: 1, label: '(1) - Péssimo 😔' },
];
const RATING_EMOJI: Record<number, string> = { 5: '🤩', 4: '😍', 3: '😐', 2: '🥺', 1: '😔' };

/** Busca por identificador (nome/telefone/protocolo) vale pra TODO o
 *  histórico — o período só se aplica quando esses campos estão vazios.
 *  Sem isso, o período default ("hoje", por data de criação) escondia o
 *  cliente buscado: atendimento criado semana passada não aparecia nem
 *  digitando o nome exato. */
function effectiveFilters(f: SearchFilters): SearchFilters {
  const hasText = Boolean(f.name?.trim() || f.contact_phone?.trim() || f.protocol_number?.trim());
  return hasText ? { ...f, date_range: undefined } : f;
}

/** Algum campo de identificação preenchido? (usado pro hint do período) */
function hasTextSearch(f: SearchFilters): boolean {
  return Boolean(f.name?.trim() || f.contact_phone?.trim() || f.protocol_number?.trim());
}

export function ConsultaPage() {
  const isAdmin = useAuthStore((st) => st.user?.profile) === 'admin';
  const [filters, setFilters] = useState<SearchFilters>(() => defaultFilters());
  // `appliedFilters` é o que efetivamente bate na API — separado do form
  // (`filters`). Só atualiza quando user clica Buscar (ou no mount inicial).
  // Mudança em `appliedFilters` causa refetch automático do useInfiniteQuery.
  //
  // `searchNonce` é um timestamp que bumpa a cada click no Buscar (mesmo se
  // os filtros forem idênticos aos anteriores). Faz parte da queryKey, então
  // cada busca vira uma entrada nova de cache — TanStack nunca devolve
  // resultado stale de buscas anteriores.
  const [appliedFilters, setAppliedFilters] = useState<SearchFilters>(filters);
  const [searchNonce, setSearchNonce] = useState(() => Date.now());
  const [openConversation, setOpenConversation] = useState<SearchTicket | null>(null);

  const channelsQ = useQuery({ queryKey: ['channels'], queryFn: listChannels });
  const tagsQ = useQuery({ queryKey: ['tags'], queryFn: listTags });
  const reasonsQ = useQuery({ queryKey: ['closing-reasons'], queryFn: listClosingReasons });
  const deptsQ = useQuery({ queryKey: ['departments'], queryFn: listDepartments });
  // Inclui EXCLUÍDOS no filtro de atendentes — histórico de ex-funcionários
  // continua consultável (queryKey própria pra não contaminar o cache das
  // telas que listam só os vivos).
  const usersQ = useQuery({
    queryKey: ['settings-users', 'com-excluidos'],
    queryFn: () => listUsers({ includeDeleted: true }),
  });

  // Scroll infinito — mesma estratégia da página /contatos (lição #72):
  // useInfiniteQuery + sentinela com IntersectionObserver. Backend retorna
  // `next_cursor` (base64url do {ts,id}) que vira o pageParam da próxima request.
  const searchQ = useInfiniteQuery({
    queryKey: ['consultation-search', appliedFilters, searchNonce],
    queryFn: ({ pageParam }) =>
      searchTickets({
        ...appliedFilters,
        limit: 30,
        ...(pageParam && { cursor: pageParam }),
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    // Sem cache entre buscas — gcTime: 0 libera memória imediatamente quando
    // a query "morre" (componente unmount ou novo nonce). staleTime: 0 garante
    // que data marcada como stale na hora.
    gcTime: 0,
    staleTime: 0,
  });

  const rawResults = searchQ.data?.pages.flatMap((p) => p.tickets) ?? [];
  const totalLoaded = rawResults.length;

  // Export CSV. Usa `appliedFilters` (o que a tabela MOSTRA), não `filters` (que
  // são os inputs, podem ter mudança não-buscada). O backend refaz a query com
  // esses filtros e devolve TODAS as linhas que casam — não os 30 carregados na
  // tela. É o ponto do recurso: baixar tudo, não só o visível.
  const [exporting, setExporting] = useState(false);
  const handleExport = async (): Promise<void> => {
    setExporting(true);
    try {
      await exportConsultation(appliedFilters, 'csv');
    } catch (err) {
      toast.error('Não foi possível exportar', {
        description: err instanceof Error ? err.message : 'Tente novamente.',
      });
    } finally {
      setExporting(false);
    }
  };

  // Sort client-side dos resultados já carregados. Cycle: null → asc → desc → null.
  // Não dispara nova request — só reordena o que já tá no array.
  const [sortBy, setSortBy] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const results = sortBy ? sortTickets(rawResults, sortBy, sortDir) : rawResults;

  function toggleSort(key: SortKey): void {
    if (sortBy !== key) {
      setSortBy(key);
      setSortDir('asc');
      return;
    }
    if (sortDir === 'asc') {
      setSortDir('desc');
      return;
    }
    // Já estava em desc → desliga o sort.
    setSortBy(null);
    setSortDir('asc');
  }

  // Sentinela no fim da tabela — quando entra na view, fetcha próxima página.
  const tableSentinelRef = useRef<HTMLTableRowElement | null>(null);
  useEffect(() => {
    const el = tableSentinelRef.current;
    if (!el || !searchQ.hasNextPage || searchQ.isFetchingNextPage) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) void searchQ.fetchNextPage();
        }
      },
      { rootMargin: '200px', threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [searchQ.hasNextPage, searchQ.isFetchingNextPage, searchQ.fetchNextPage]);

  // Lookup do canal por id — usado pra mostrar o NOME do canal (displayName)
  // na coluna "Canal" da tabela, ao invés do número de telefone.
  const channelById = new Map((channelsQ.data ?? []).map((c) => [c.id, c]));

  return (
    <div className="flex h-full min-w-0 flex-1 flex-col bg-n-50">
      {/* Header com título + ações de export — fixo no topo da página */}
      <header className="flex flex-shrink-0 items-center justify-between border-b border-n-200 bg-n-0 px-5 py-3">
        <div>
          <h1 className="font-bold text-[16px] text-n-900">Consulta</h1>
          <p className="text-[12px] text-n-500">
            {searchQ.isLoading
              ? 'Carregando…'
              : searchQ.hasNextPage
                ? `Mostrando ${totalLoaded} resultados (role pra carregar mais)`
                : `${totalLoaded} ${totalLoaded === 1 ? 'resultado' : 'resultados'}`}
          </p>
        </div>
      </header>

      {/* Filtros — barra horizontal acima dos resultados.
          Grid responsivo: 4 cols em telas largas, 2 em médias, 1 em mobile.
          Todos os campos compartilham FieldLabel + altura h-9 + border-n-300. */}
      <section className="flex-shrink-0 border-b border-n-200 bg-n-0 px-5 py-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="sm:col-span-2">
            <DateRangeFilter filters={filters} setFilters={setFilters} />
          </div>
          <TextFilter
            label="Nome do contato"
            value={filters.name ?? ''}
            onChange={(v) => setFilters({ ...filters, name: v || undefined })}
          />
          <TextFilter
            label="Telefone"
            value={filters.contact_phone ?? ''}
            onChange={(v) => setFilters({ ...filters, contact_phone: v || undefined })}
          />
          <TextFilter
            label="Protocolo"
            value={filters.protocol_number ?? ''}
            onChange={(v) => setFilters({ ...filters, protocol_number: v || undefined })}
          />
          <MultiSelect
            label="Status"
            options={[
              { value: 'active', label: 'Ativos' },
              { value: 'pending', label: 'Pendentes' },
              { value: 'closed', label: 'Fechados' },
            ]}
            values={filters.status ?? []}
            onChange={(vals) => setFilters({ ...filters, status: vals as SearchFilters['status'] })}
          />
          <MultiSelect
            label="Canais"
            options={(channelsQ.data ?? []).map((c) => ({
              value: c.id,
              label: c.displayName ?? c.displayNumber ?? `Canal #${c.id}`,
            }))}
            values={filters.whatsapp_number_ids ?? []}
            onChange={(vals) => setFilters({ ...filters, whatsapp_number_ids: vals as number[] })}
          />
          <MultiSelect
            label="Atendentes"
            options={(usersQ.data ?? []).map((u) => ({
              value: u.id,
              label: u.deleted ? `${u.name} (excluído)` : u.name,
            }))}
            values={filters.agent_ids ?? []}
            onChange={(vals) => setFilters({ ...filters, agent_ids: vals as number[] })}
          />
          <MultiSelect
            label="Departamentos"
            options={(deptsQ.data ?? []).map((d) => ({ value: d.id, label: d.name }))}
            values={filters.department_ids ?? []}
            onChange={(vals) => setFilters({ ...filters, department_ids: vals as number[] })}
          />
          <MultiSelect
            label="Etiquetas"
            options={(tagsQ.data ?? []).map((t) => ({ value: t.id, label: t.name }))}
            values={filters.tag_ids ?? []}
            onChange={(vals) => setFilters({ ...filters, tag_ids: vals as number[] })}
          />
          <MultiSelect
            label="Motivos de fechamento"
            options={(reasonsQ.data ?? []).map((r) => ({ value: r.id, label: r.name }))}
            values={filters.closing_reason_ids ?? []}
            onChange={(vals) => setFilters({ ...filters, closing_reason_ids: vals as number[] })}
          />
          <MultiSelect
            label="Notas"
            options={RATING_OPTIONS}
            values={filters.ratings ?? []}
            onChange={(vals) => setFilters({ ...filters, ratings: vals as number[] })}
          />
          {/* Linha de ações. Ordem: Limpar · ⬇ (exportar CSV, só o ícone pra
              caber) · Buscar. Com 11 filtros a grade fecha exata em 3 linhas,
              então as ações ganham a linha inteira (col-span-full) alinhadas à
              direita — sem o span elas caíam soltas na 1ª coluna. */}
          <div className="flex items-end justify-end gap-3 sm:col-span-2 lg:col-span-4">
            <button
              type="button"
              onClick={() => {
                const fresh = defaultFilters();
                setFilters(fresh);
                setAppliedFilters(fresh);
                setSearchNonce(Date.now());
              }}
              className="whitespace-nowrap pb-2 text-[13px] font-semibold text-n-500 hover:text-n-700 hover:underline"
            >
              Limpar filtros
            </button>
            {/* Exportar segue só do admin (perfil com Histórico só consulta). */}
            {isAdmin && (
              <Button
                variant="outline"
                onClick={handleExport}
                // Desabilitado enquanto exporta OU enquanto a busca não trouxe nada
                // (sem resultado não há o que baixar).
                disabled={exporting || searchQ.isLoading || totalLoaded === 0}
                title="Exportar dados em CSV"
                aria-label="Exportar dados em CSV"
                // Só o ícone (13px) rende mais baixo que os botões com texto (a
                // linha de 16,5px do texto os deixa em 33px). `min-h-[33px]` casa
                // a altura com o "Buscar" ao lado; `px-3` deixa o toque quadrado.
                className="min-h-[33px] px-3"
              >
                {exporting ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Download size={14} />
                )}
              </Button>
            )}
            <Button
              onClick={() => {
                setAppliedFilters(effectiveFilters({ ...filters }));
                setSearchNonce(Date.now());
              }}
              disabled={searchQ.isFetching && !searchQ.isFetchingNextPage}
            >
              {searchQ.isFetching && !searchQ.isFetchingNextPage ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <SearchIcon size={14} />
              )}
              Buscar
            </Button>
          </div>
        </div>
      </section>

      {/* Card da tabela com altura fixa = ocupa o espaço vertical disponível,
          scroll INTERNO ativa quando lista cresce. Mesma estratégia de /contatos
          (lição #72): wrapper `flex min-h-0 flex-1 flex-col overflow-hidden p-5`,
          card `flex min-h-0 flex-col overflow-hidden`, scroll-area `min-h-0 flex-1
          overflow-auto`, thead `sticky top-0 z-10`. */}
      <main className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden p-5">
        {searchQ.isLoading && (
          <div className="rounded-md border border-n-200 bg-n-0 p-12 text-center text-[13px] text-n-500">
            <Loader2 size={18} className="mx-auto animate-spin text-n-400" />
          </div>
        )}
        {!searchQ.isLoading && totalLoaded === 0 && (
          <div className="rounded-md border border-n-200 bg-n-0 p-12 text-center text-[13px] text-n-500">
            Nenhum ticket bate com os filtros.
          </div>
        )}
        {totalLoaded > 0 && (
          <div className="flex min-h-0 min-w-0 flex-col overflow-hidden rounded-md border border-n-200 bg-n-0 shadow-sm">
            <div className="min-h-0 flex-1 overflow-auto table-scroll-x">
              <table className="w-full min-w-[1200px] text-[12px]">
                <thead className="border-b border-n-200 bg-n-50 text-left">
                  <tr>
                    <th className="sticky top-0 z-20 border-b border-n-200 bg-n-50 px-[14px] py-[10px] text-[11px] font-bold uppercase tracking-[.05em] text-n-500 whitespace-nowrap">
                      #
                    </th>
                    <SortableTh
                      label="Status"
                      sortKey="status"
                      currentKey={sortBy}
                      dir={sortDir}
                      onToggle={toggleSort}
                    />
                    <SortableTh
                      label="Nome"
                      sortKey="name"
                      currentKey={sortBy}
                      dir={sortDir}
                      onToggle={toggleSort}
                    />
                    <th className="sticky top-0 z-20 border-b border-n-200 bg-n-50 px-[14px] py-[10px] text-[11px] font-bold uppercase tracking-[.05em] text-n-500 whitespace-nowrap">
                      Telefone
                    </th>
                    <th className="sticky top-0 z-20 border-b border-n-200 bg-n-50 px-[14px] py-[10px] text-[11px] font-bold uppercase tracking-[.05em] text-n-500 whitespace-nowrap">
                      Canal
                    </th>
                    <SortableTh
                      label="Atendente"
                      sortKey="agent"
                      currentKey={sortBy}
                      dir={sortDir}
                      onToggle={toggleSort}
                    />
                    <th className="sticky top-0 z-20 border-b border-n-200 bg-n-50 px-[14px] py-[10px] text-[11px] font-bold uppercase tracking-[.05em] text-n-500 whitespace-nowrap">
                      Dept
                    </th>
                    <th className="sticky top-0 z-20 border-b border-n-200 bg-n-50 px-[14px] py-[10px] text-[11px] font-bold uppercase tracking-[.05em] text-n-500 whitespace-nowrap">
                      Etiquetas
                    </th>
                    <SortableTh
                      label="Criado"
                      sortKey="created"
                      currentKey={sortBy}
                      dir={sortDir}
                      onToggle={toggleSort}
                    />
                    <SortableTh
                      label="Fechado"
                      sortKey="closed"
                      currentKey={sortBy}
                      dir={sortDir}
                      onToggle={toggleSort}
                    />
                    <SortableTh
                      label="Motivo"
                      sortKey="reason"
                      currentKey={sortBy}
                      dir={sortDir}
                      onToggle={toggleSort}
                    />
                    <SortableTh
                      label="Nota"
                      sortKey="rating"
                      currentKey={sortBy}
                      dir={sortDir}
                      onToggle={toggleSort}
                    />
                    <th className="sticky right-0 top-0 z-30 border-b border-n-200 bg-n-50 px-[14px] py-[10px] text-center text-[11px] font-bold uppercase tracking-[.05em] text-n-500 whitespace-nowrap shadow-[-6px_0_8px_-6px_rgba(15,23,42,0.12)]">
                      Ver
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((t) => (
                    <tr
                      key={t.id}
                      className="group border-b border-n-100 last:border-b-0 hover:bg-n-50"
                    >
                      <td className="px-[14px] py-[11px] text-n-700 whitespace-nowrap">
                        {t.protocol_number ?? t.id}
                      </td>
                      <td className="px-[14px] py-[11px]">
                        <StatusChip status={t.status} />
                      </td>
                      <td className="px-[14px] py-[11px] text-n-900 whitespace-nowrap">
                        {t.contact_name ?? '—'}
                      </td>
                      <td className="px-[14px] py-[11px] text-n-600 whitespace-nowrap">
                        {t.contact_phone ?? '—'}
                      </td>
                      <td className="px-[14px] py-[11px] text-n-700 whitespace-nowrap">
                        {channelById.get(t.whatsapp_number_id)?.displayName ??
                          t.whatsapp_display_number ??
                          '—'}
                      </td>
                      <td className="px-[14px] py-[11px] text-n-700 whitespace-nowrap">
                        {t.agent_name ?? '—'}
                      </td>
                      <td className="px-[14px] py-[11px] text-n-700 whitespace-nowrap">
                        {t.department_name ?? '—'}
                      </td>
                      <td className="px-[14px] py-[11px]">
                        <div className="flex flex-wrap gap-1">
                          {t.tags.map((tg) => (
                            <span
                              key={tg.id}
                              className="rounded-pill px-2 py-[2px] font-semibold text-[10px]"
                              style={{ background: `${tg.color}1f`, color: tg.color }}
                            >
                              {tg.name}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-[14px] py-[11px] text-n-600 whitespace-nowrap">
                        {formatDateTime(t.created_at)}
                      </td>
                      <td className="px-[14px] py-[11px] text-n-600 whitespace-nowrap">
                        {t.closed_at ? formatDateTime(t.closed_at) : '—'}
                      </td>
                      <td className="px-[14px] py-[11px] text-n-700">{t.closing_reason ?? '—'}</td>
                      <td
                        className="px-[14px] py-[11px] text-n-700 whitespace-nowrap"
                        title={
                          t.rating != null
                            ? RATING_OPTIONS.find((o) => o.value === t.rating)?.label
                            : undefined
                        }
                      >
                        {t.rating != null ? `${RATING_EMOJI[t.rating] ?? ''} ${t.rating}` : '—'}
                      </td>
                      <td className="sticky right-0 z-10 bg-n-0 px-[14px] py-[11px] shadow-[-6px_0_8px_-6px_rgba(15,23,42,0.12)] group-hover:bg-n-50">
                        <div className="flex items-center justify-center">
                          <button
                            type="button"
                            onClick={() => setOpenConversation(t)}
                            title="Ver conversa"
                            aria-label="Ver conversa"
                            className="flex h-8 w-8 items-center justify-center rounded text-n-600 hover:text-n-900"
                          >
                            <MessageSquareText size={15} strokeWidth={1.75} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {searchQ.hasNextPage && (
                    <tr ref={tableSentinelRef}>
                      <td
                        colSpan={13}
                        className="px-[14px] py-4 text-center text-[12px] text-n-500"
                      >
                        <span className="inline-flex items-center gap-2">
                          <Loader2 size={14} className="animate-spin" /> Carregando mais…
                        </span>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>

      {openConversation && (
        <ConversationModal ticket={openConversation} onClose={() => setOpenConversation(null)} />
      )}
    </div>
  );
}

type SortKey = 'status' | 'name' | 'agent' | 'created' | 'closed' | 'reason' | 'rating';

/** Compara dois tickets segundo a chave + direção. NULL value sempre vai pro
 *  fim (independente da direção). Status segue ordem lógica: ativo → pendente → fechado. */
function sortTickets(list: SearchTicket[], key: SortKey, dir: 'asc' | 'desc'): SearchTicket[] {
  const statusOrder: Record<string, number> = { active: 0, pending: 1, closed: 2 };
  const getValue = (t: SearchTicket): string | number | null => {
    switch (key) {
      case 'status':
        return statusOrder[t.status] ?? 99;
      case 'name':
        return t.contact_name?.toLowerCase() ?? null;
      case 'agent':
        return t.agent_name?.toLowerCase() ?? null;
      case 'created':
        return t.created_at ?? null;
      case 'closed':
        return t.closed_at ?? null;
      case 'reason':
        return t.closing_reason?.toLowerCase() ?? null;
      case 'rating':
        return t.rating;
    }
  };
  const sign = dir === 'asc' ? 1 : -1;
  return [...list].sort((a, b) => {
    const av = getValue(a);
    const bv = getValue(b);
    // NULL sempre por último.
    if (av === null && bv === null) return 0;
    if (av === null) return 1;
    if (bv === null) return -1;
    if (av < bv) return -1 * sign;
    if (av > bv) return 1 * sign;
    return 0;
  });
}

/** Cabeçalho de coluna clicável — mostra chevron up/down/both conforme estado. */
function SortableTh({
  label,
  sortKey,
  currentKey,
  dir,
  onToggle,
}: {
  label: string;
  sortKey: SortKey;
  currentKey: SortKey | null;
  dir: 'asc' | 'desc';
  onToggle: (key: SortKey) => void;
}) {
  const active = currentKey === sortKey;
  return (
    <th className="sticky top-0 z-20 border-b border-n-200 bg-n-50 px-[14px] py-[10px] text-[11px] font-bold uppercase tracking-[.05em] text-n-500 whitespace-nowrap">
      <button
        type="button"
        onClick={() => onToggle(sortKey)}
        className={`flex items-center gap-1 ${active ? 'text-brand-600' : 'hover:text-n-700'}`}
      >
        {label}
        {active ? (
          dir === 'asc' ? (
            <ChevronUp size={12} />
          ) : (
            <ChevronDown size={12} />
          )
        ) : (
          <ChevronsUpDown size={12} className="text-n-400" />
        )}
      </button>
    </th>
  );
}

function StatusChip({ status }: { status: string }) {
  const map: Record<string, string> = {
    active: 'bg-success-50 text-success-700',
    pending: 'bg-warning-50 text-warning-700',
    closed: 'bg-n-100 text-n-600',
  };
  const labelMap: Record<string, string> = {
    active: 'Ativo',
    pending: 'Pendente',
    closed: 'Fechado',
  };
  return (
    <span
      className={`rounded-pill px-2 py-[2px] font-semibold text-[10px] uppercase ${map[status] ?? 'bg-n-100'}`}
    >
      {labelMap[status] ?? status}
    </span>
  );
}

/** Label padrão dos filtros — mesma tipografia em todo lugar. */
function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <span className="font-semibold text-[11px] uppercase tracking-[0.05em] text-n-500">
      {children}
    </span>
  );
}

/** Formata ISO pra `DD/MM/YYYY HH:MM` em PT-BR (timezone local). */
function formatDateTime(iso: string): string {
  const d = new Date(iso);
  const date = d.toLocaleDateString('pt-BR');
  const time = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  return `${date} ${time}`;
}

/** Converte ISO UTC pra string `YYYY-MM-DD` na timezone local — input `type=date` espera local. */
function isoToLocalYMD(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function DateRangeFilter({
  filters,
  setFilters,
}: {
  filters: SearchFilters;
  setFilters: (f: SearchFilters) => void;
}) {
  // Controlled component — lê direto de filters. Sem state interno, reage
  // a setFilters externos (ex: Limpar filtros volta pra hoje automaticamente).
  const start = filters.date_range?.start ? isoToLocalYMD(filters.date_range.start) : '';
  const end = filters.date_range?.end ? isoToLocalYMD(filters.date_range.end) : '';

  // CRÍTICO: `new Date('2026-05-19')` interpreta como UTC (00:00Z). Pro Brasil
  // (UTC-3), isso vira 21h do dia ANTERIOR — busca não acha nada do dia.
  // Sufixar `T00:00:00` força timezone local; pro end usamos 23:59:59.999
  // pra incluir o dia inteiro.
  const update = (nextStart: string, nextEnd: string): void => {
    setFilters({
      ...filters,
      date_range: {
        ...(nextStart && { start: new Date(`${nextStart}T00:00:00`).toISOString() }),
        ...(nextEnd && { end: new Date(`${nextEnd}T23:59:59.999`).toISOString() }),
      },
    });
  };

  return (
    <div className="flex flex-col gap-1.5">
      <FieldLabel>Período (criado em)</FieldLabel>
      <div className="flex gap-2">
        <Input type="date" value={start} onChange={(e) => update(e.target.value, end)} />
        <Input type="date" value={end} onChange={(e) => update(start, e.target.value)} />
      </div>
      {hasTextSearch(filters) && (
        <p className="text-[11px] leading-tight text-warning-700">
          Ignorado nesta busca — nome/telefone/protocolo procura em todo o histórico.
        </p>
      )}
    </div>
  );
}

function TextFilter({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <FieldLabel>{label}</FieldLabel>
      <Input value={value} onChange={(e) => onChange(e.target.value)} />
    </label>
  );
}

function MultiSelect<V extends string | number>({
  label,
  options,
  values,
  onChange,
}: {
  label: string;
  options: Array<{ value: V; label: string }>;
  values: V[];
  onChange: (next: V[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  // Fecha ao clicar fora do popup.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent): void => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={wrapperRef} className="relative flex flex-col gap-1.5">
      <FieldLabel>{label}</FieldLabel>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex h-9 items-center justify-between gap-2 rounded-md border border-n-300 bg-n-0 px-3 text-left text-sm text-n-700 hover:border-n-400 focus:border-brand-500 focus:outline-none focus:ring-[3px] focus:ring-brand-500/25"
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <span className="truncate">
          {values.length === 0 ? (
            <span className="text-n-400">Selecionar…</span>
          ) : (
            `${values.length} selecionado${values.length === 1 ? '' : 's'}`
          )}
        </span>
        <span className={`text-n-400 transition ${open ? 'rotate-180' : ''}`}>▾</span>
      </button>
      {open && (
        <div
          role="listbox"
          className="absolute top-full left-0 right-0 z-40 mt-1 max-h-[220px] overflow-y-auto rounded-md border border-n-200 bg-n-0 py-1 shadow-md"
        >
          {options.length === 0 && (
            <div className="px-3 py-2 text-[12px] text-n-400">Nenhuma opção.</div>
          )}
          {options.map((o) => {
            const active = values.includes(o.value);
            return (
              <label
                key={String(o.value)}
                className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-[13px] text-n-800 hover:bg-n-50"
              >
                <input
                  type="checkbox"
                  checked={active}
                  onChange={() =>
                    onChange(active ? values.filter((v) => v !== o.value) : [...values, o.value])
                  }
                />
                <span className="truncate">{o.label}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

const INITIAL_BATCH = 50;
const SCROLL_BATCH = 50;

/**
 * Modal de histórico de mensagens do ticket — read-only.
 *
 * Escopo: histórico COMPLETO do contato (cross-ticket) — usuário pode ver
 * msgs anteriores/posteriores de outros tickets do mesmo contato. Ao abrir,
 * scroll é posicionado automaticamente na ÚLTIMA msg do ticket clicado e
 * essas msgs ganham um destaque visual (estilo "ir pra mensagem" do WhatsApp).
 *
 * - 1ª página: `INITIAL_BATCH` (50) msgs mais recentes.
 * - Se a última msg do ticket NÃO estiver na 1ª página, fetcha próximas
 *   automaticamente até encontrar (ou esgotar).
 * - Depois disso, scroll fica sob controle do user; sentinela no topo
 *   continua puxando mais antigas sob demanda.
 */
function ConversationModal({
  ticket,
  onClose,
}: {
  ticket: SearchTicket;
  onClose: () => void;
}) {
  useEscapeClose(true, onClose);
  const q = useInfiniteQuery({
    queryKey: ['consulta-messages', ticket.id],
    queryFn: ({ pageParam }) =>
      listMessages(ticket.id, {
        limit: pageParam === undefined ? INITIAL_BATCH : SCROLL_BATCH,
        scope: 'contact',
        ...(typeof pageParam === 'number' && { before: pageParam }),
      }),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
  });

  // Altura do conteúdo ANTES de buscar o bloco antigo — usada pra recolocar o
  // scroll depois que ele entra. Vide `handleScroll` / o useLayoutEffect abaixo.
  const prevScrollHeightRef = useRef<number | null>(null);
  // Container do scroll — usado pra auto-scroll pra msg-alvo na 1ª render.
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  // Bolha-alvo (última msg do ticket clicado) — scrollIntoView.
  const targetMsgRef = useRef<HTMLDivElement | null>(null);
  const didInitialScrollRef = useRef(false);

  /**
   * Carrega o bloco antigo ao chegar perto do topo — mesma estratégia do
   * ThreadPanel do /atendimento (`handleScroll` + `prevScrollHeightRef`).
   *
   * Era um IntersectionObserver numa sentinela no topo, e cascateava: as msgs
   * antigas entram ACIMA do viewport, e sem recolocar o scroll a sentinela
   * continuava visível → o observer disparava de novo → carregava a conversa
   * inteira de uma vez (relatado em contato com histórico grande).
   *
   * O gatilho por `scrollTop` resolve a cascata (o usuário precisa rolar de
   * novo) e ainda funciona em aba oculta, onde o observer não dispara.
   */
  const handleScroll = (): void => {
    const el = scrollContainerRef.current;
    if (!el) return;
    if (el.scrollTop < 120 && q.hasNextPage && !q.isFetchingNextPage) {
      prevScrollHeightRef.current = el.scrollHeight;
      void q.fetchNextPage();
    }
  };

  // Recoloca o scroll depois que o bloco antigo entra, preservando o que o
  // usuário estava lendo. Só age quando `handleScroll` armou a referência —
  // o auto-fetch que procura a msg-alvo (abaixo) não arma, então não interfere.
  useLayoutEffect(() => {
    const el = scrollContainerRef.current;
    if (!el || prevScrollHeightRef.current === null) return;
    el.scrollTop = el.scrollHeight - prevScrollHeightRef.current;
    prevScrollHeightRef.current = null;
  }, [q.data?.pages.length]);

  // Mensagens em ordem cronológica (oldest → newest) — backend já entrega
  // cada page em ordem cronológica (faz `rows.reverse()` após DESC). Só
  // precisamos inverter a ordem das PAGES (page 0 é o batch mais recente).
  // Mesma estratégia do ThreadPanel.tsx do /atendimento.
  const allMessages = [...(q.data?.pages ?? [])].reverse().flatMap((p) => p.messages);

  // Events do escopo (só na 1ª página da query — backend não devolve em pages
  // posteriores). Frontend mistura cronologicamente com as msgs e renderiza
  // banner inline + alimenta o modal de timeline.
  //
  // Como os eventos vêm inteiros e as mensagens vêm em blocos, cada banner
  // espera o pedaço de conversa dele chegar na tela — senão os antigos se
  // amontoam acima do bloco carregado (vide eventos-do-bloco-carregado.ts).
  // Com todas as páginas carregadas (é o caso da impressão) não há piso.
  const pisoDeBanner = pisoDosBanners(
    allMessages.map((m) => m.created_at),
    q.hasNextPage ?? false,
  );
  const quandoAconteceu = (e: { created_at: string }): string => e.created_at;
  const transferEvents = dentroDoBlocoCarregado(
    q.data?.pages[0]?.transfer_events ?? [],
    quandoAconteceu,
    pisoDeBanner,
  );
  const chatbotEvents = dentroDoBlocoCarregado(
    q.data?.pages[0]?.chatbot_events ?? [],
    (e) => e.started_at,
    pisoDeBanner,
  );
  const reopenEvents = dentroDoBlocoCarregado(
    q.data?.pages[0]?.reopen_events ?? [],
    quandoAconteceu,
    pisoDeBanner,
  );

  // Última msg do ticket clicado dentro do que já carregamos.
  const lastTicketMsgId = (() => {
    for (let i = allMessages.length - 1; i >= 0; i--) {
      if (allMessages[i]?.ticket_id === ticket.id) return allMessages[i]?.id ?? null;
    }
    return null;
  })();

  // Auto-fetch até encontrar a última msg do ticket. Se já achou, scroll lá.
  // Se não achou e ainda há próximas páginas, dispara fetchNextPage.
  useEffect(() => {
    if (didInitialScrollRef.current) return;
    if (q.isLoading || q.isFetchingNextPage) return;
    // Se ainda há páginas e não achou msg do ticket, busca mais.
    if (lastTicketMsgId === null) {
      if (q.hasNextPage) {
        void q.fetchNextPage();
      } else {
        // Esgotou sem encontrar — fallback: scroll pro final.
        const el = scrollContainerRef.current;
        if (el) el.scrollTop = el.scrollHeight;
        didInitialScrollRef.current = true;
      }
      return;
    }
    // Encontrou — scroll pra bolha-alvo e marca como feito.
    const targetEl = targetMsgRef.current;
    if (targetEl) {
      targetEl.scrollIntoView({ block: 'end', behavior: 'auto' });
      didInitialScrollRef.current = true;
    }
  }, [q.isLoading, q.isFetchingNextPage, q.hasNextPage, q.fetchNextPage, lastTicketMsgId]);

  // Estado da preparação de impressão (loading enquanto pre-fetcha todas as páginas).
  const [isPreparingPrint, setIsPreparingPrint] = useState(false);
  // Toggle de visibilidade dos banners de sistema (transfer + chatbot) na thread.
  // Default: exibidos. Atendente pode ocultar pra ler só as mensagens.
  const [showSystemBanners, setShowSystemBanners] = useState(false);

  // Pré-fetcha TODAS as páginas restantes antes de chamar window.print().
  // Sem isso, só as páginas já carregadas em scroll/auto-search apareceriam
  // no PDF. `result.hasNextPage` é a referência fresca pós-fetch — o
  // q.hasNextPage do closure fica stale entre awaits.
  async function handlePrint(): Promise<void> {
    if (isPreparingPrint) return;
    setIsPreparingPrint(true);
    try {
      let hasMore = q.hasNextPage;
      // Safety cap: previne loop infinito se algum bug deixar hasNextPage
      // sempre true. 200 páginas × 50 msgs = 10K msgs, mais que suficiente.
      let safety = 200;
      while (hasMore && safety > 0) {
        const result = await q.fetchNextPage();
        hasMore = result.hasNextPage ?? false;
        safety -= 1;
      }
      // Tick pro React renderizar todas as novas bolhas no DOM antes do print.
      await new Promise((r) => setTimeout(r, 250));
      window.print();
    } finally {
      setIsPreparingPrint(false);
    }
  }

  // Itens unificados (msgs + transfer + chatbot start/end) ordenados por timestamp.
  // Cada chatbot_execution gera 1 ou 2 itens (start sempre; end só se ended_at).
  // Quando `showSystemBanners=false`, passa arrays vazios pra esconder os banners.
  const threadItems = buildThreadItems(
    allMessages,
    showSystemBanners ? transferEvents : [],
    showSystemBanners ? chatbotEvents : [],
    showSystemBanners ? reopenEvents : [],
  );
  const grouped = groupItemsByDay(threadItems);

  return (
    <div
      className="consulta-print-root fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* Print CSS — escopo restrito ao modal. body * fica `visibility: hidden`
          e só a subtree do modal volta visível; `position: static` + reset de
          tamanhos faz o conteúdo fluir natural em A4. Botões com `consulta-print-hidden`
          somem da página impressa. */}
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          .consulta-print-root, .consulta-print-root * { visibility: visible !important; }
          .consulta-print-root { position: absolute !important; inset: 0 !important; background: white !important; padding: 0 !important; display: block !important; }
          .consulta-print-content { box-shadow: none !important; border: 0 !important; border-radius: 0 !important; width: 100% !important; max-width: 100% !important; height: auto !important; overflow: visible !important; }
          .consulta-print-scroll { overflow: visible !important; max-height: none !important; flex: initial !important; }
          .consulta-print-hidden { display: none !important; }
          /* Força Chrome a imprimir backgrounds e borders coloridos das bolhas/players
             (default 'economy' strippa cores → bolha vira branca sem contorno visível). */
          .consulta-print-root, .consulta-print-root * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
        }
      `}</style>
      <div className="consulta-print-content flex h-[80vh] w-[1100px] max-w-[95vw] flex-col overflow-hidden rounded-lg bg-n-0 shadow-lg">
        <header className="flex flex-shrink-0 items-center justify-between border-n-200 border-b px-5 py-3">
          <div>
            <h3 className="font-bold text-[16px] text-n-900">
              {ticket.contact_name ?? 'Sem nome'}
              <span className="ml-2 font-mono text-[12px] font-normal text-n-500">
                #{ticket.protocol_number ?? ticket.id}
              </span>
            </h3>
            <p className="text-[12px] text-n-500">
              {ticket.contact_phone ?? '—'} · {ticket.whatsapp_display_number ?? '—'}
            </p>
          </div>
          <div className="consulta-print-hidden flex items-center gap-1">
            <button
              type="button"
              onClick={() => setShowSystemBanners((v) => !v)}
              className="flex h-8 w-8 items-center justify-center rounded-md text-n-500 hover:bg-n-100"
              aria-label={
                showSystemBanners
                  ? 'Ocultar informações do sistema'
                  : 'Exibir informações do sistema'
              }
              title={
                showSystemBanners
                  ? 'Ocultar informações do sistema'
                  : 'Exibir informações do sistema'
              }
            >
              <MessageCircleMore size={16} className={showSystemBanners ? 'text-brand-600' : ''} />
            </button>
            <button
              type="button"
              onClick={() => void handlePrint()}
              disabled={isPreparingPrint}
              className="flex h-8 w-8 items-center justify-center rounded-md text-n-500 hover:bg-n-100 disabled:opacity-50"
              aria-label="Imprimir ou salvar como PDF"
              title={
                isPreparingPrint ? 'Carregando histórico completo…' : 'Imprimir ou salvar como PDF'
              }
            >
              {isPreparingPrint ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Printer size={16} />
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex h-8 w-8 items-center justify-center rounded-md text-n-500 hover:bg-n-100"
              aria-label="Fechar"
            >
              <X size={16} />
            </button>
          </div>
        </header>

        {/* Área das mensagens — mesmo bg + padding do ThreadPanel do /atendimento. */}
        <div
          ref={scrollContainerRef}
          onScroll={handleScroll}
          className="consulta-print-scroll flex flex-1 flex-col overflow-y-auto overflow-x-hidden bg-n-50 px-5 py-4"
        >
          {/* Aviso no TOPO — o carregamento é disparado por `handleScroll`. */}
          {q.hasNextPage && (
            <div className="flex justify-center py-3 text-[11px] text-n-400">
              {q.isFetchingNextPage ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 size={12} className="animate-spin" /> Carregando mais antigas…
                </span>
              ) : (
                'Role pra carregar mais antigas'
              )}
            </div>
          )}
          {q.isLoading && (
            <div className="flex flex-1 items-center justify-center">
              <Loader2 size={18} className="animate-spin text-n-400" />
            </div>
          )}
          {!q.isLoading && allMessages.length === 0 && (
            <div className="flex flex-1 items-center justify-center text-[12px] text-n-500">
              Sem mensagens.
            </div>
          )}
          {/* Grupos por dia — mistura msgs + transfer + chatbot banners. Marker
              invisível (h-0) é inserido logo APÓS a bolha da última msg do
              ticket clicado — usado pra scrollIntoView na 1ª renderização. */}
          {grouped.map((group) => (
            <div key={group.label} className="flex flex-col gap-2.5">
              <div className="my-2 flex justify-center">
                <span className="rounded-pill border border-n-200 bg-n-0 px-3.5 py-1 text-[11px] font-semibold text-n-600">
                  {group.label}
                </span>
              </div>
              {group.items.map((item) => {
                if (item.kind === 'transfer') {
                  return <TransferBanner key={`t-${item.data.id}`} event={item.data} />;
                }
                if (item.kind === 'reopen') {
                  return <ReopenBanner key={`r-${item.data.id}`} event={item.data} />;
                }
                if (item.kind === 'chatbot') {
                  return (
                    <ChatbotBanner
                      key={`c-${item.data.id}-${item.phase}`}
                      event={item.data}
                      phase={item.phase}
                    />
                  );
                }
                const m = item.data;
                const isAnchor = m.id === lastTicketMsgId;
                return (
                  <Fragment key={`m-${m.id}`}>
                    <MessageBubble message={m} />
                    {isAnchor && (
                      <div ref={targetMsgRef} aria-hidden="true" className="h-0 w-full" />
                    )}
                  </Fragment>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Banners inline (mensagens de sistema renderizadas direto na thread)
// ---------------------------------------------------------------------------

/** Item da thread — mensagem ou banner (transferência/chatbot/reabertura). */
type ThreadItem =
  | { kind: 'message'; ts: string; data: ChatMessage }
  | { kind: 'transfer'; ts: string; data: TransferEvent }
  | { kind: 'chatbot'; ts: string; phase: 'start' | 'end'; data: ChatbotEvent }
  | { kind: 'reopen'; ts: string; data: ReopenEvent };

interface ItemDayGroup {
  label: string;
  items: ThreadItem[];
}

/** Mescla mensagens + transfer_events + chatbot_events ordenados por timestamp.
 *  Cada chatbot_execution gera 1 ou 2 itens (start sempre; end só se ended_at).
 *  Tie-breaker: quando 2 itens têm o MESMO ts, chatbot `start` vai ANTES das
 *  mensagens (banner aparece logo acima das respostas que o bot gerou), e
 *  chatbot `end` vai DEPOIS. Sem isso, o executor cria a row de execução e
 *  envia as msgs no mesmo instante → DB grava com a mesma timestamp e a ordem
 *  ficaria por inserção, mostrando o banner depois das respostas (errado). */
function buildThreadItems(
  messages: ChatMessage[],
  transferEvents: TransferEvent[],
  chatbotEvents: ChatbotEvent[],
  reopenEvents: ReopenEvent[] = [],
): ThreadItem[] {
  const items: ThreadItem[] = [];
  for (const m of messages) items.push({ kind: 'message', ts: m.created_at, data: m });
  for (const e of transferEvents) items.push({ kind: 'transfer', ts: e.created_at, data: e });
  for (const e of reopenEvents) items.push({ kind: 'reopen', ts: e.created_at, data: e });
  for (const e of chatbotEvents) {
    items.push({ kind: 'chatbot', ts: e.started_at, phase: 'start', data: e });
    if (e.ended_at) {
      items.push({ kind: 'chatbot', ts: e.ended_at, phase: 'end', data: e });
    }
  }
  items.sort((a, b) => {
    const tsCmp = a.ts.localeCompare(b.ts);
    if (tsCmp !== 0) return tsCmp;
    const priority = (i: ThreadItem): number => {
      if (i.kind === 'chatbot') return i.phase === 'start' ? -1 : 1;
      return 0;
    };
    return priority(a) - priority(b);
  });
  return items;
}

function groupItemsByDay(items: ThreadItem[]): ItemDayGroup[] {
  const groups: ItemDayGroup[] = [];
  for (const item of items) {
    const label = dayLabel(item.ts);
    const last = groups[groups.length - 1];
    if (last && last.label === label) {
      last.items.push(item);
    } else {
      groups.push({ label, items: [item] });
    }
  }
  return groups;
}

/** Banner inline de reabertura (mesma anatomia dos demais banners). */
function ReopenBanner({ event }: { event: ReopenEvent }) {
  const time = new Date(event.created_at).toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });
  const agendado = event.kind === 'scheduled_open';
  const quem = event.actor_user_name;
  const text = agendado
    ? quem
      ? `Atendimento aberto por agendamento de ${quem}`
      : 'Atendimento aberto por agendamento'
    : quem
      ? `Atendimento reaberto por ${quem}`
      : 'Atendimento reaberto';
  const Icone = agendado ? CalendarClock : Undo2;
  return (
    <div className="my-1 flex justify-center px-4">
      <span className="inline-flex max-w-[90%] items-center gap-1.5 rounded-pill border border-brand-200 bg-brand-50 px-4 py-1.5 text-[12px] font-medium text-brand-700">
        <Icone size={13} />
        <span className="truncate">{text}</span>
        <span className="font-normal text-brand-500">·</span>
        <span className="whitespace-nowrap font-normal text-brand-500">{time}</span>
      </span>
    </div>
  );
}

/** Banner inline de transferência (mesma anatomia do TransferBanner do /atendimento). */
function TransferBanner({ event }: { event: TransferEvent }) {
  const time = new Date(event.created_at).toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });
  return (
    <div className="my-1 flex justify-center px-4">
      <span className="inline-flex max-w-[90%] items-center gap-1.5 rounded-pill border border-brand-200 bg-brand-50 px-4 py-1.5 text-[12px] font-medium text-brand-700">
        <ArrowRightLeft size={13} />
        <span className="truncate">{formatTransferText(event)}</span>
        <span className="font-normal text-brand-500">·</span>
        <span className="whitespace-nowrap font-normal text-brand-500">{time}</span>
      </span>
    </div>
  );
}

function formatTransferText(e: TransferEvent): string {
  if (e.motivo === 'atendente_offline') {
    const quem = e.from_user_name ?? 'Atendente';
    return e.to_user_name
      ? `${quem} ficou offline — atendimento transferido para ${e.to_user_name}`
      : `${quem} ficou offline — atendimento voltou para Pendentes`;
  }
  const from = e.from_user_name;
  const to = e.to_user_name;
  const toDept = e.to_department_name;
  if (to) {
    if (from && from !== to) return `Atendimento transferido de ${from} para ${to}`;
    if (from === to) return `Atendimento puxado por ${to}`;
    return `Atendimento transferido para ${to}`;
  }
  if (e.queued) {
    if (toDept) return `Atendimento enviado pro canal de ${toDept}`;
    return 'Atendimento enviado pro canal';
  }
  if (toDept) return `Departamento definido como ${toDept}`;
  return 'Atendimento transferido';
}

/** Banner inline de execução de chatbot (start ou end). */
function ChatbotBanner({
  event,
  phase,
}: {
  event: ChatbotEvent;
  phase: 'start' | 'end';
}) {
  const ts = phase === 'start' ? event.started_at : (event.ended_at ?? event.started_at);
  const time = new Date(ts).toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });
  const name = event.chatbot_name ?? 'sem nome';
  const text = (() => {
    if (phase === 'start') return `Automação ${name} foi executada`;
    switch (event.status) {
      case 'completed':
        return `Automação ${name} concluída`;
      case 'transferred':
        return `Automação ${name} transferiu pra atendimento humano`;
      case 'abandoned':
        return `Automação ${name} abandonada`;
      case 'errored':
        return `Automação ${name} falhou${event.error_message ? `: ${event.error_message}` : ''}`;
      default:
        return `Automação ${name}`;
    }
  })();
  return (
    <div className="my-1 flex justify-center px-4">
      <span className="inline-flex max-w-[90%] items-center gap-1.5 rounded-pill border border-brand-200 bg-brand-50 px-4 py-1.5 text-[12px] font-medium text-brand-700">
        <Bot size={13} />
        <span className="truncate">{text}</span>
        <span className="font-normal text-brand-500">·</span>
        <span className="whitespace-nowrap font-normal text-brand-500">{time}</span>
      </span>
    </div>
  );
}
